import {
    ChangeDetectorRef,
    Component, HostListener,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { ChangeFilterParams, IItems, ISearchFilters } from '@app/models/interfaces/ISearchFilters';
import { FiltersService } from '@app/services/filters.service';
import { StoreService } from '@app/services/store.service';
import { ProductSiteRegionService } from '@app/services/product-site-region.service';
import { ProductTypeEnum } from '@app/models/enums/product-type.enum';
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { SelectItem } from '@app/modules/tf-filters';
import { Subscription } from "rxjs";
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticsService } from "../../../../services/analytics.service";

@Component({
    selector: 'tf-search-filters',
    templateUrl: './search-filters.component.html',
    styleUrls: ['./search-filters.component.scss']
})
export class TfSearchFiltersComponent implements OnChanges, OnDestroy {
    @Input() searchFilters: Array<ISearchFilters>;
    @Input() totalHits: number;
    @Input() keyword: string;
    @Input() type: "assay" | "array";
    // variables used when keyword is not found but data is returned for suggested keywords
    @Input() keywordUsed: string;
    @Input() suggestedKeywords: string;
    @Input() suggestedKeywordsArray: string[];
    @ViewChild('geCrossReactivity') crossReactivityRef: TemplateRef<Element>;
    @ViewChild('geAttributes') attributesRef: TemplateRef<Element>;
    @ViewChild('geAttributesFS') attributesRefFS: TemplateRef<Element>;
    @ViewChild('searchTips') searchTipsRef: TemplateRef<Element>;
    @ViewChild('snpAssayType') assayTypeRef: TemplateRef<Element>;

    items: IItems[] = [];
    isSelected: boolean = false
    filters: SelectItem[] = [];
    renderedLabels: SelectItem[] = [];
    filterSubscription: Subscription;
    searchResultCountSubscription: Subscription;
    routeSubscription: Subscription;
    isShowClicked: boolean = false;
    originalKeyword: string = "";
    isMobile: boolean = true;
    isFilterPillOpened = false;
    totalFiltersCount = 0;
    hasFilterParams: boolean = false;
    filterName: string[] = [];
    hideForCustomArray: boolean = false;

    constructor(
        public storeService: StoreService,
        private filterService: FiltersService,
        private cdr: ChangeDetectorRef,
        private ngbModalService: NgbModal,
        private router: Router,
        private route: ActivatedRoute,
        private productSiteRegionService: ProductSiteRegionService,
        private analyticsService: AnalyticsService
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.checkUrl();
        this.detectWindowWidth();
        this.items = this.filterService.setItems(changes['searchFilters']?.currentValue, this.type, this.hasFilterParams, this.filterName);
        this.searchResultCountSubscription = this.storeService.searchResultCountsState$.subscribe(state => {
            this.totalFiltersCount = this.isAssay() ? state.assayCount : state.arrayCount;
        });
        this.storeService.setModalFilters(this.items, this.totalFiltersCount);
        this.filterSubscription = this.storeService.tempFilters$.subscribe(state => {
            this.isSelected = state.filters.length > 0;
            this.filters = state.filters;
            this.renderedLabels = this.filters.length > 2 ? this.getOptionLabels() : this.filters;
            this.isFilterPillOpened = state.isFilterPillOpened;
        });

        const params = {
            totalHits: this.totalHits,
            keyword: this.keyword,
            filters: this.storeService.getFilters(),
            type: this.type,
            productSubType: this.storeService.searchResults$.getValue()?.productSubType
        };
        this.analyticsService.triggerSearchEvent(params);

    }

    checkUrl() {
        this.routeSubscription = this.route.queryParams.subscribe((params) => {
            this.hasFilterParams = !!params['filters'];
            const filteredNames = params['filters']?.match(/(\b[^~]+)/gm);

            filteredNames?.forEach(name => {
                const filterName = name.split(":")[0]
                this.filterName.push(filterName)
            });
        })
        if (this.router.url.includes('snp') && this.route.snapshot.queryParams['customArray'] === 'true') {
            this.hideForCustomArray = true;
        }
    }

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }

    ngOnDestroy() {
        this.filterSubscription.unsubscribe();
        this.searchResultCountSubscription.unsubscribe();
        this.routeSubscription.unsubscribe();
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.detectWindowWidth();
    }

    handleShowLessBtn() {
        this.isShowClicked = !this.isShowClicked;
        this.renderedLabels = this.getOptionLabels();
    }

    getOptionLabels() {
        return this.isShowClicked ? this.filters : this.filters.slice(0, 2);
    }

    onChange(event: ChangeFilterParams, filterIndex: number) {
        this.storeService.setLocalFilters({isFilterPillOpened: true})
        this.storeService.setFilters(filterIndex, event);
        this.storeService.setFilterPillState(filterIndex, this.isFilterPillOpened);
    }

    onClosed() {
        this.storeService.setFilterPillState();
    }

    detectWindowWidth() {
        this.isMobile = window.innerWidth < 767;
    }

    isPopover(productType: string, filterName: string): boolean {
        switch (productType) {
            case "GE":
                return filterName === "Attributes" || filterName === "Remove cross-reactivity";
            case "SNP":
                return filterName === "Assay type";
            default:
                return false;
        }
    }

    getPopoverContentRef(productType: string, filterName: string): TemplateRef<any> {
        switch (productType) {
            case "GE":
                switch (filterName) {
                    case "Attributes":
                        if (this.productSiteRegionService.isFisherSci()) {
                            return this.attributesRefFS;
                        }
                        return this.attributesRef ;
                    case "Remove cross-reactivity":
                        return this.crossReactivityRef;
                    default:
                        return null;
                }
            case "SNP":
                switch (filterName) {
                    case "Assay type":
                        return this.assayTypeRef;
                    default:
                        return null;
                }
            default:
                return null;
        }
    }

    getPopoverClass(productType: string, filterName: string): string {
        switch (productType) {
            case "GE":
                return this.isAttribute(filterName) ? "tf-ngb-popover width-415" : "tf-ngb-popover";
            case "SNP":
                return "tf-ngb-popover width-415 text-sm-start";
            default:
                return "";
        }
    }

    isAttribute(filterName: string) {
        return filterName === "Attributes";
    }

    isAssay() {
        return this.type === "assay";
    }

    onFiltersClick() {
        this.loadFiltersMobileModule().then((module) => {
            this.openFiltersModal(module.TfSearchFilterMobileComponent);
        })
    }

    openFiltersModal(component){

        this.ngbModalService.open(component, {
            animation: false,
            modalDialogClass: "search-filter-mobile",
            windowClass: "filter-modal",
            injector: Injector.create({
                providers: [{
                    provide: "modalData",
                    useValue: {
                        totalHits: this.totalHits,
                    }
                }]
            })
        });

    }

    async loadFiltersMobileModule(): Promise<any> {

        const module = await import("../../modules/search-filters-mobile");

        return new Promise((res) => {
            res(module);
        });

    }

    deleteItem(item: SelectItem) {
        this.storeService.removeTempFilter(item);
    }

    deleteAll() {
        this.storeService.resetTempFilters();
    }

    getMoreLabel() {
        return this.isShowClicked ? "Show less" : `+${this.filters.length - 2}`;
    }

    suggestedKeywordClass() {
        let suggestedKeywordClass = "";
        if (this.suggestedKeywords && this.suggestedKeywords.length > 0) {
            suggestedKeywordClass = "tf-suggested-keyword";
        }
        return suggestedKeywordClass;
    }

    isSuggestingKeyword(): boolean {
        let validSuggestedKeyword: boolean = false;
        if (this.suggestedKeywords && this.suggestedKeywords.length > 0) {
            validSuggestedKeyword = true;
        }
        return validSuggestedKeyword;
    }

    getSeparator(index: number) {
        if (this.suggestedKeywordsArray.length <= 1) {
            return "";
        }
        let separator: string = '';
        const arrayLength = this.suggestedKeywordsArray?.length;
        separator = index < arrayLength - 2 ? ',' : '';
        return separator;
    }

    getLastSeparator(isLast: boolean) {
        return isLast && this.suggestedKeywordsArray?.length > 1 ? 'or' : "";
    }

    shouldDisplaySimilarSearches(): boolean {
        this.suggestedKeywordsArray = this.suggestedKeywordsArray.filter(keyword => this.keywordUsed?.toUpperCase() !== keyword.toUpperCase());
        if (this.suggestedKeywordsArray) {
            return this.suggestedKeywordsArray.length > 0;
        }
        return false;
    }

    redirect(keyword: string) {
        const productType = this.storeService.getProductType();
        if (productType === ProductTypeEnum.ge) {
            const productType = this.type === 'assay' ? 'ge' : 'gx';
            this.router.navigate(['gene-expression', this.type, 'query'], {
                queryParams: {
                    keyword: keyword,
                    productType: productType,
                    productSubtype: productType
                }
            }).then(r => r);
        } else if (productType === ProductTypeEnum.snp) {
            this.router.navigate(['snp', 'assay', 'query'], {
                queryParams: {
                    keyword: keyword,
                    productType: 'gt',
                    productSubtype: 'gt'
                }
            }).then(r => r);
        } else if (productType === ProductTypeEnum.cnv) {
            this.router.navigate(['cnv', 'assay', 'query'], {
                queryParams: {
                    keyword: keyword,
                    productType: 'cnv',
                    productSubtype: 'cnv'
                }
            }).then(r => r);
        } else if (productType === ProductTypeEnum.microrna) {
            let productSubType = this.storeService.searchResults$.getValue().productSubType;
            switch (productSubType) {
                case 'microrna_advanced':
                    productSubType = 'microrna-advanced';
                    break;
                case 'microrna_primary':
                    productSubType = 'microrna-primary';
                    break;
                case 'microrna_classic':
                    productSubType = 'microrna-classic';
                    break;
            }
            this.router.navigate(['microrna', 'assay', productSubType], {
                queryParams: {
                    keyword: keyword
                }
            }).then(r => r);
        }

    }

    handleSearch(event: string, filterIndex: number, filterName: string) {
        if (this.isNeedToCallSearchApi(filterName)) {
            this.storeService.handleFilterSearch(event, filterName, filterIndex);
        }
    }

    isNeedToCallSearchApi(filterName: string) {
        return filterName === "PrimaryGeneSymbolCaps" || filterName === "genesymbolcaps_facet" || filterName === "geneSymbolCaps"  || filterName === "otherSpecies";
    }

    getSearchTipClass(): string {
        return "tf-popover-search-tips width-415"
    }

    hasClearBtn(productType: string, name: string) {

        switch (productType) {
            case "GE":
                return name.toLowerCase() === 'genes' || name.toLowerCase() === 'species' || name.toLowerCase() === 'remove cross-reactivity'
            case "SNP":
                return name.toLowerCase() === 'genes';
            case "CNV":
                return name.toLowerCase() === 'genes';
            case 'ADVANCED_MIRNA':
                return name.toLowerCase().includes('species (compatible)');
            case 'CLASSIC_MIRNA':
                return name.toLowerCase().includes('species');
            default:
                return false;
        }

    }

    getSearchPlaceholder(name: string): string {
        switch (name.toLowerCase()) {
            case 'species (compatible)':
                return 'Search Species';
            default:
                return null;
        }
    }

    hasVirtualScroll(productType: string, item: IItems) {
        switch (productType) {
            case "CNV":
                return item.navigators?.length > 10;
            default:
                return item.hasSearch || item.navigators?.length > 7;
        }

    }

    getResultsLabel() {
        return this.totalHits > 1 ? 'results for' : 'result for';
    }

}
